import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPolicyTransactionList,
  PolicyTransactionDto,
} from "utils/api/policyTransactionApi";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";
import { StageType } from "utils/data/enum";

export const dtoToRecord = (dto: PolicyTransactionDto): PolicyManagementRecord => ({
  ...dto,
  uniqueIdentifier: String(dto.PolicyTransactionID),
  isNational: !!dto.IsMAST,
  isLocked: !!dto.LockedByUserName,
  lockedBy: dto.LockedByUserName ?? undefined,
  lockedDate: dto.LockedDate ?? undefined,
  errors: [],
  policyNotes: [],
  policyActivity: [],
  policyAuditLog: [],
  parties: [],
  returnNote: null,
});

// Drop empty/null entries; flatten arrays to comma-joined strings so the SP
// can STRING_SPLIT them (mirrors the StateCodeList convention). Numbers/booleans
// (e.g. IsHOI 1/0, IsLocked, IsOnHold) are passed through as-is rather than stringified,
// since the backend binds those criteria fields to bool/int, not string.
// Loader merges arbitrary criteria (header inputs + chip / advanced fragments)
// into one bag, so accept `unknown` here and narrow per type.
function serializeFilters(
  filters: Record<string, unknown>
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v == null) continue;
    if (Array.isArray(v)) {
      if (v.length === 0) continue;
      out[k] = v.join(",");
    } else if (typeof v === "number" || typeof v === "boolean") {
      out[k] = v;
    } else if (typeof v === "string" && v.trim() !== "") {
      out[k] = v;
    }
  }
  return out;
}

export function usePolicyTransactionList(
  stageTypeCode: StageType,
  listCount: number,
  requestedPage: number,
  sortColumn: string,
  filters: Record<string, unknown> = {},
  enabled: boolean = true
) {
  const [{ profileSettings }] = useProfileSettingsCache();
  const userTimeZone = profileSettings?.userTimeZone ?? "";
  const [records, setRecords] = useState<PolicyManagementRecord[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reloadToken, setReloadToken] = useState(0);
  const reqIdRef = useRef(0);

  // Re-run the current search on demand (e.g. after a force-promote succeeds).
  const refetch = useCallback(() => setReloadToken((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) return;
    if (!userTimeZone) return;
    const myReq = ++reqIdRef.current;
    setIsLoading(true);
    getPolicyTransactionList({
      UserTimeZone: userTimeZone,
      StageTypeCode: stageTypeCode,
      RequestedPage: requestedPage,
      ListCount: listCount,
      SortColumn: sortColumn,
      ...serializeFilters(filters),
    })
      .then((dtos) => {
        if (reqIdRef.current !== myReq) return;
        // TotalRows / TotalPages are whole-query totals (not per-page) duplicated
        // on every row by the SP's window-function; read from any row.
        const first = dtos[0];
        setTotalRows(first?.TotalRows ?? dtos.length);
        setTotalPages(first?.TotalPages ?? (dtos.length > 0 ? 1 : 0));
        setRecords(dtos.map(dtoToRecord));
        setIsLoading(false);
      })
      .catch(() => {
        if (reqIdRef.current === myReq) setIsLoading(false);
      });
  }, [stageTypeCode, listCount, requestedPage, sortColumn, userTimeZone, filters, enabled, reloadToken]);

  return { records, totalRows, totalPages, isLoading, setRecords, refetch };
}

export default usePolicyTransactionList;